import { configureCache, GrowthBook } from '@growthbook/growthbook'

import type { Environment } from '../App'

const GROWTHBOOK_CLIENT_KEY: Record<Environment, string> = {
  tst: 'sdk-QJuaoobMsTbl4Xy',
  acc: 'sdk-3RHhusOm4TMqnpv8',
  prd: 'sdk-8TP0RBlivw2TPxD',
}

const ACTIVE_CARDS_FEATURE = 'CH-active-cards'
const OVERVIEW_TAB_FEATURE = 'CH-overview-tab'
const CANCELLATION_FLOW_FEATURE = 'CH-cancellation-flow'
const ITEMS_IN_CART_MAX_ITEMS_FEATURE = 'CH-items-in-cart'
const RECENTLY_BROWSED_MAX_ITEMS_FEATURE = 'CH-recently-browsed'
const SIZE_PASSPORT_FEATURE = 'CH-size-passport'
const WARDROBE_FEATURE = 'CH-wardrobe'
const NBA_CARDS_FEATURE = 'NBA_cards_final'
const DEFAULT_ITEMS_IN_CART_MAX_ITEMS = 3
const DEFAULT_RECENTLY_BROWSED_MAX_ITEMS = 2

configureCache({ disableCache: true })

let growthbook: GrowthBook | null = null
let growthbookEnv: Environment | null = null

function getGrowthBook(env: Environment): GrowthBook {
  if (!growthbook || growthbookEnv !== env) {
    growthbook = new GrowthBook({
      apiHost: 'https://cdn.growthbook.io',
      clientKey: GROWTHBOOK_CLIENT_KEY[env],
      enableDevMode: env !== 'prd',
      // `init` ignores this flag, so every call site below has to ask for
      // streaming explicitly to keep the SSE subscription alive.
      subscribeToChanges: true,
    })
    growthbookEnv = env
  }

  return growthbook
}

/**
 * Gates the Active Cards on Overview and Order Details. Targetable per role
 * and per store through the `role` / `locationId` attributes.
 */
export async function isActiveCardsEnabled(
  env: Environment,
  locationId: string,
  role?: 'customer' | 'style-advisor',
): Promise<boolean> {
  const gb = getGrowthBook(env)
  gb.setAttributes({
    ...gb.getAttributes(),
    locationId,
    role,
  })
  await gb.init({ streaming: true })
  return gb.isOn(ACTIVE_CARDS_FEATURE)
}

/**
 * Gates the Overview tab for customers (RP-6184). Style advisors always get
 * the tab, so callers must not consult this for them. Boolean, off by default.
 */
export async function isOverviewTabEnabled(
  env: Environment,
  locationId: string,
  role?: 'customer' | 'style-advisor',
): Promise<boolean> {
  const gb = getGrowthBook(env)
  gb.setAttributes({
    ...gb.getAttributes(),
    locationId,
    role,
  })
  await gb.init({ streaming: true })
  return gb.isOn(OVERVIEW_TAB_FEATURE)
}

export async function isCancellationFlowEnabled(
  env: Environment,
  locationId: string,
  role?: 'customer' | 'style-advisor',
): Promise<boolean> {
  const gb = getGrowthBook(env)
  gb.setAttributes({
    ...gb.getAttributes(),
    locationId,
    role,
  })
  await gb.init({ streaming: true })
  return gb.isOn(CANCELLATION_FLOW_FEATURE)
}

export async function getItemsInCartMaxItems(
  env: Environment,
  locationId: string,
  role?: 'customer' | 'style-advisor',
): Promise<number> {
  const gb = getGrowthBook(env)
  gb.setAttributes({
    ...gb.getAttributes(),
    locationId,
    role,
  })
  await gb.init({ streaming: true })

  const value = gb.getFeatureValue(
    ITEMS_IN_CART_MAX_ITEMS_FEATURE,
    DEFAULT_ITEMS_IN_CART_MAX_ITEMS,
  )
  const numericValue = Number(value)

  if (!Number.isFinite(numericValue)) {
    return DEFAULT_ITEMS_IN_CART_MAX_ITEMS
  }

  return Math.min(9, Math.max(0, Math.trunc(numericValue)))
}

export async function getRecentlyBrowsedMaxItems(
  env: Environment,
  locationId: string,
  role?: 'customer' | 'style-advisor',
): Promise<number> {
  const gb = getGrowthBook(env)
  gb.setAttributes({
    ...gb.getAttributes(),
    locationId,
    role,
  })
  await gb.init({ streaming: true })

  const value = gb.getFeatureValue(
    RECENTLY_BROWSED_MAX_ITEMS_FEATURE,
    DEFAULT_RECENTLY_BROWSED_MAX_ITEMS,
  )
  const numericValue = Number(value)

  if (!Number.isFinite(numericValue)) {
    return DEFAULT_RECENTLY_BROWSED_MAX_ITEMS
  }

  return Math.min(9, Math.max(0, Math.trunc(numericValue)))
}

export async function isSizePassportEnabled(
  env: Environment,
  locationId: string,
  role?: 'customer' | 'style-advisor',
): Promise<boolean> {
  const gb = getGrowthBook(env)
  gb.setAttributes({
    ...gb.getAttributes(),
    locationId,
    role,
  })
  await gb.init({ streaming: true })

  return gb.isOn(SIZE_PASSPORT_FEATURE)
}

export async function isWardrobeEnabled(
  env: Environment,
  locationId: string,
  role?: 'customer' | 'style-advisor',
): Promise<boolean> {
  const gb = getGrowthBook(env)
  gb.setAttributes({
    ...gb.getAttributes(),
    locationId,
    role,
  })
  await gb.init({ streaming: true })

  return gb.isOn(WARDROBE_FEATURE)
}

/**
 * Gates the Next Best Action card on Overview (RP-6063). Targetable per role
 * and per store through the `role` / `locationId` attributes, same as the
 * other Overview flags above.
 */
export async function isNbaCardsEnabled(
  env: Environment,
  locationId: string,
  role?: 'customer' | 'style-advisor',
): Promise<boolean> {
  const gb = getGrowthBook(env)
  gb.setAttributes({
    ...gb.getAttributes(),
    locationId,
    role,
  })
  await gb.init({ streaming: true })

  return gb.isOn(NBA_CARDS_FEATURE)
}
